import React, { useState } from 'react';
import { Home, Search, LibraryBig, Menu, X, ArrowRightLeft, PhoneCall, UserCheck, FileChartColumn, Calculator, AudioLines } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';


interface NavbarProps {
  isOpen: boolean;
  toggleMenu: () => void;
  onValuesChange: (values: { apiKey: string; selectedOption: string }) => void;
  userRole: string | null; // Added userRole to props
}

const Navbar: React.FC<NavbarProps> = ({ isOpen, toggleMenu, onValuesChange, userRole }) => { // Destructure userRole
  const location = useLocation();
  const [apiKey, setApiKey] = useState('');
  const [selectedOption, setSelectedOption] = useState('');

  const handleApiKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setApiKey(value);
    onValuesChange({ apiKey: value, selectedOption });
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedOption(value);
    onValuesChange({ apiKey, selectedOption: value });
  };

  // Define navigation items with their allowed roles
  const navItems = [
    { icon: <Home size={20} />, label: 'Home', path: '/', roles: ['admin'] },
    // { icon: <Search size={20} />, label: 'Product search', path: '/productsearch', roles: ['admin'] }, // Example: if specific to admin
    { icon: <LibraryBig size={20} />, label: 'Knowledgebase', path: '/Knowledgebase', roles: ['admin'] },
    // { icon: <ArrowRightLeft size={20} />, label: 'Schema', path: '/Schemamapper', roles: ['admin'] }, // Example: if specific to admin
    { icon: <PhoneCall size={20} />, label: 'Invisible Assistant', path: '/InvisibleAI', roles: ['admin'] },
    { icon: <PhoneCall size={20} />, label: 'AlMajdouie Assistant', path: '/AlMajdouie_call', roles: ['almajdouie'] },

    { icon: <UserCheck size={20} />, label: 'Campaigns', path: '/campaigns', roles: ['admin'] },
    { icon: <FileChartColumn size={20} />, label: 'Call Analytics', path: '/AnalyticsDasboard', roles: ['admin'] },
    { icon: <FileChartColumn size={20} />, label: 'Call Analytics', path: '/AlmajdouieCallAnalytics', roles: ['almajdouie'] },
    { icon: <AudioLines size={20} />, label: 'Campaign Analytics', path: '/CampaignAnalytics', roles: ['admin'] },
    { icon: <AudioLines size={20} />, label: 'Campaign Analytics', path: '/Almajdouie_Analytics', roles: ['almajdouie'] },
    { icon: <Calculator size={20} />, label: 'Product Estimator', path: '/Calculator', roles: ['admin'] },
    { icon: <Calculator size={20} />, label: 'Service Slots', path: '/ServiceSlots', roles: ['almajdouie'] },

    { icon: <PhoneCall size={20} />, label: 'Invisible Assistant', path: '/Lead_Assistant', roles: ['leadAssistance'] },
    { icon: <FileChartColumn size={20} />, label: 'Call Analytics', path: '/Lead_CallAnalytics', roles: ['leadAssistance'] },
    { icon: <AudioLines size={20} />, label: 'Campaign Analytics', path: '/Lead_Campaign_Analytics', roles: ['leadAssistance'] },

    { icon: <PhoneCall size={20} />, label: 'Pedal Assistant', path: '/Pedal_Assistant', roles: ['PedalAssistance'] },
    { icon: <FileChartColumn size={20} />, label: 'Call Analytics', path: '/Pedal_CallAnalytics', roles: ['PedalAssistance'] },
    { icon: <AudioLines size={20} />, label: 'Campaign Analytics', path: '/Pedal_Campaign_Analytics', roles: ['PedalAssistance'] },

    { icon: <PhoneCall size={20} />, label: 'Mai Dubai Assistant', path: '/MaiDubai_Assistant', roles: ['MaiDubaiAssistance'] },
    { icon: <FileChartColumn size={20} />, label: 'Call Analytics', path: '/MaiDubai_CallAnalytics', roles: ['MaiDubaiAssistance'] },
  ];

  const dropdownOptions = [
    { value: "", label: 'Please select the model' },
    { value: 'llama-3.1-70b-versatile', label: 'Llama 3.1' },
    { value: 'llama-3.3-70b-versatile', label: 'Llama 3.3' },
  ];

  // Filter nav items based on the user's role
  const filteredNavItems = navItems.filter(item =>
    userRole && item.roles.includes(userRole)
  );

  return (
    <nav className={`fixed max-w-[260px] top-[77px] left-0 h-[calc(100vh-115px)] bg-white border-r border-gray-200 text-white transition-all duration-300`}>
      <button onClick={toggleMenu} className={` text-black w-full  flex ${isOpen ? 'justify-end pr-5 pt-4' : 'mt-5 w-full justify-center absolute '}`}>
        {isOpen ? <X size={24} /> : <Menu size={24} />}
      </button>
      <div className="flex flex-col h-full">

        <ul className="flex-1 px-2 pt-14 ">
          {filteredNavItems.map((item) => ( // Use filteredNavItems here
            <li key={item.path}>
              <Link
                to={item.path}
                className={`flex items-center gap-4 px-2 py-4 rounded-lg text-black transition-colors text-sm ${
                  location.pathname === item.path ? ' text-[#4161c8]' : ''
                }`}
              >
                <span className={`${location.pathname === item.path ? 'text-primary' : 'text-gray-[#0f1312]'}`}>
                  {item.icon}
                </span>
                <span className={` ${!isOpen && 'hidden'}`}>
                  {item.label}
                </span>
              </Link>
            </li>
          ))}
        </ul>

        {/* Input Fields Section */}
        <div className="mt-auto p-4 border-t border-gray-300 bg-blue-200/20">
          <div className={`space-y-3  ${!isOpen && 'hidden'}`}>
            <input
              type="text"
              placeholder="Enter Your Groq API Key"
              value={apiKey}
              onChange={handleApiKeyChange}
              className="w-full px-3 text-xs py-2 border border-gray-300 bg-white rounded-lg text-black placeholder-gray-400 focus:outline-none"
            />
            <select
              value={selectedOption}
              onChange={handleSelectChange}
              className="w-full px-3 py-2 text-xs border border-gray-300 bg-white rounded-lg text-black focus:outline-none"
            >
              {dropdownOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;